/* ── emmera-routine-builder.js ───────────────────────────────────────────── */
/* Créateur de Routine Emmera — EmmeraRoutineBuilder class                   */

class EmmeraRoutineBuilder {
  constructor() {
    this.routine     = [];   // [{id, variantId, title, price, image, tags, handle}]
    this.phase       = null;
    this.moment      = 'les-deux';
    this.typeFilter  = 'tous';
    this.allProducts = [];

    this.discountMap  = { 2: 5, 3: 10, 4: 15 };
    this.promoCodes   = { 5: 'ROUTINE5', 10: 'ROUTINE10', 15: 'ROUTINE15' };
    this.savedKey     = 'emmera_routine_saved';

    this.phaseHints = {
      'menstruelle':  'Phase menstruelle : ta peau peut être plus sensible et sèche. Privilégie les soins doux, sans parfum ni exfoliants.',
      'folliculaire': 'Phase folliculaire : ta peau retrouve de l\'éclat et de l\'énergie. Moment idéal pour les actifs régénérants.',
      'ovulatoire':   'Phase ovulatoire : ta peau est au top — elle tolère mieux les actifs puissants et brille naturellement.',
      'luteale':      'Phase lutéale : le sébum augmente, les imperfections peuvent apparaître. Mise sur les soins purifiants et équilibrants.'
    };

    this.compatibilityRules = [
      {
        actifs: ['retinol', 'aha'],
        message: 'Retinol et AHA peuvent être irritants ensemble. Conseillé : alterner les soirs ou utiliser matin/soir séparément.'
      },
      {
        actifs: ['retinol', 'vitamine-c'],
        message: 'Retinol (soir) et Vitamine C (matin) fonctionnent mieux à des moments différents pour maximiser leur efficacité.'
      },
      {
        actifs: ['aha', 'bha'],
        message: 'AHA et BHA associés peuvent être trop exfoliants. Alterner les soirs plutôt qu\'utiliser simultanément.'
      }
    ];
  }

  /* ────────────────────── INIT ────────────────────── */

  init() {
    const panel = document.getElementById('emrbPanel');
    this.currency = (panel && panel.dataset.currency) ? panel.dataset.currency : 'EUR';
    this.bindPhaseSelector();
    this.bindMomentSelector();
    this.bindTypeFilter();
    this.bindPanelToggle();
    this.bindAddCartBtn();
    this.bindSaveBtn();
    this.handleUrlParam();
    this.loadSavedRoutine();
    this.updatePanel();
  }

  /* ────────────────────── SELECTORS ────────────────────── */

  bindPhaseSelector() {
    const pills = document.querySelectorAll('.emrb-phase-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        pills.forEach(p => p.classList.remove('emrb-pill--active'));
        pill.classList.add('emrb-pill--active');
        this.phase = pill.dataset.phase;
        this.showPhaseHint();
        this.fetchAndRenderProducts();
      });
    });
  }

  bindMomentSelector() {
    const pills = document.querySelectorAll('.emrb-moment-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        pills.forEach(p => p.classList.remove('emrb-pill--active'));
        pill.classList.add('emrb-pill--active');
        this.moment = pill.dataset.moment;
        this.renderFilteredProducts();
      });
    });
  }

  showPhaseHint() {
    const hint = document.getElementById('emrbPhaseHint');
    if (!hint) return;
    if (this.phase && this.phaseHints[this.phase]) {
      hint.textContent = this.phaseHints[this.phase];
      hint.classList.add('emrb-phase-hint--visible');
    } else {
      hint.classList.remove('emrb-phase-hint--visible');
    }
  }

  /* ────────────────────── TYPE FILTER ────────────────────── */

  bindTypeFilter() {
    const pills = document.querySelectorAll('.emrb-type-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        pills.forEach(p => p.classList.remove('emrb-type-pill--active'));
        pill.classList.add('emrb-type-pill--active');
        this.typeFilter = pill.dataset.type;
        this.renderFilteredProducts();
      });
    });
  }

  /* ────────────────────── PRODUCTS ────────────────────── */

  async fetchAndRenderProducts() {
    if (!this.phase) {
      this.showNoPhase();
      return;
    }

    this.showLoading();

    try {
      // Fetch all products and filter by tags client-side
      let url = '/products.json?limit=250';
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erreur réseau');
      const data = await res.json();

      this.allProducts = (data.products || []).filter(p => {
        const tags = (p.tags || '').split(',').map(t => t.trim().toLowerCase());
        // Must have at least one phase tag matching current phase
        return tags.includes(`phase-${this.phase}`);
      }).map(p => ({
        id:        p.id,
        variantId: p.variants?.[0]?.id,
        title:     p.title,
        price:     parseFloat(p.variants?.[0]?.price || 0),
        image:     p.images?.[0]?.src || '',
        tags:      (p.tags || '').split(',').map(t => t.trim().toLowerCase()),
        handle:    p.handle
      }));

      this.renderFilteredProducts();
    } catch (err) {
      console.error('[EmmeraRoutineBuilder] fetch error:', err);
      this.showError();
    }
  }

  renderFilteredProducts() {
    const grid = document.getElementById('emrbProductsGrid');
    if (!grid) return;

    let products = [...this.allProducts];

    // Filter by moment
    if (this.moment !== 'les-deux') {
      products = products.filter(p => {
        // If product has moment tags, filter; if no moment tags, show always
        const hasMomentTag = p.tags.some(t => t === 'matin' || t === 'soir');
        if (!hasMomentTag) return true;
        return p.tags.includes(this.moment);
      });
    }

    // Filter by type
    if (this.typeFilter !== 'tous') {
      products = products.filter(p => p.tags.includes(this.typeFilter));
    }

    if (products.length === 0) {
      grid.innerHTML = this.renderEmpty();
      return;
    }

    grid.innerHTML = products.map(p => this.renderProductCard(p)).join('');
    this.bindProductButtons();
  }

  renderProductCard(product) {
    const isAdded  = this.routine.some(r => r.id === product.id);
    const actifs   = product.tags.filter(t =>
      ['retinol','aha','bha','vitamine-c','acide-hyaluronique','niacinamide','bakuchiol','cbd','huile-rosier','acide-lactique'].includes(t)
    );
    const typeTag  = product.tags.find(t =>
      ['nettoyant','serum','creme','huile','masque','complement','brume','baume','exfoliant','contour'].includes(t)
    );
    const priceStr = product.price > 0 ? `${product.price.toFixed(2)} ${this.currency}` : '—';
    const img      = product.image
      ? `<img class="emrb-product-card__img" src="${product.image}" alt="${this.escape(product.title)}" loading="lazy">`
      : `<div class="emrb-product-card__img" style="background:var(--emrb-ivory)"></div>`;

    return `
      <article class="emrb-product-card${isAdded ? ' emrb-product-card--added' : ''}" data-product-id="${product.id}">
        <div class="emrb-product-card__img-wrap">${img}</div>
        <div class="emrb-product-card__body">
          ${typeTag ? `<p class="emrb-product-card__type">${typeTag}</p>` : ''}
          <p class="emrb-product-card__title">${this.escape(product.title)}</p>
          ${actifs.length ? `<div class="emrb-product-card__actifs">${actifs.map(a => `<span class="emrb-actif-tag">${a}</span>`).join('')}</div>` : ''}
          <div class="emrb-product-card__footer">
            <span class="emrb-product-card__price">${priceStr}</span>
            <button class="emrb-product-card__btn" data-action="${isAdded ? 'remove' : 'add'}" data-product-id="${product.id}">
              ${isAdded ? 'Retirer' : 'Ajouter'}
            </button>
          </div>
        </div>
        <span class="emrb-product-card__added-badge">Ajouté</span>
      </article>
    `;
  }

  renderEmpty() {
    return `
      <div class="emrb-catalog-empty">
        <p>Aucun produit trouvé pour cette combinaison.<br>
        Essaie de modifier le filtre ou de sélectionner une autre phase.</p>
      </div>
    `;
  }

  showLoading() {
    const grid = document.getElementById('emrbProductsGrid');
    if (grid) grid.innerHTML = `
      <div class="emrb-catalog-loading">
        <div class="emrb-spinner"></div>
        <p>Chargement des produits...</p>
      </div>
    `;
  }

  showNoPhase() {
    const grid = document.getElementById('emrbProductsGrid');
    if (grid) grid.innerHTML = `
      <div class="emrb-no-phase-hint">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="1.5"/>
          <path d="M24 14v10l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <p>Sélectionne ta phase du cycle pour voir les soins recommandés.</p>
      </div>
    `;
  }

  showError() {
    const grid = document.getElementById('emrbProductsGrid');
    if (grid) grid.innerHTML = `
      <div class="emrb-catalog-empty">
        <p>Impossible de charger les produits. Recharge la page.</p>
      </div>
    `;
  }

  bindProductButtons() {
    document.querySelectorAll('.emrb-product-card__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = parseInt(btn.dataset.productId, 10);
        const action    = btn.dataset.action;
        if (action === 'add') {
          this.addProduct(productId);
        } else {
          this.removeProduct(productId);
        }
      });
    });
  }

  /* ────────────────────── ROUTINE MANAGEMENT ────────────────────── */

  addProduct(productId) {
    if (this.routine.some(r => r.id === productId)) return;
    const product = this.allProducts.find(p => p.id === productId);
    if (!product) return;

    this.routine.push(product);
    this.updateCardState(productId, true);
    this.updatePanel();
  }

  removeProduct(productId) {
    this.routine = this.routine.filter(r => r.id !== productId);
    this.updateCardState(productId, false);
    this.updatePanel();
  }

  updateCardState(productId, added) {
    const card = document.querySelector(`.emrb-product-card[data-product-id="${productId}"]`);
    if (!card) return;
    const btn = card.querySelector('.emrb-product-card__btn');
    card.classList.toggle('emrb-product-card--added', added);
    if (btn) {
      btn.textContent  = added ? 'Retirer' : 'Ajouter';
      btn.dataset.action = added ? 'remove' : 'add';
      btn.addEventListener('click', () => {
        if (added) this.removeProduct(productId);
        else this.addProduct(productId);
      }, { once: true });
    }
  }

  /* ────────────────────── PANEL UPDATE ────────────────────── */

  updatePanel() {
    this.updateRoutineItems();
    this.updateProgress();
    this.updatePriceSummary();
    this.checkCompatibility();
    this.updateAddCartBtn();
    this.updateMobileHandle();
  }

  updateRoutineItems() {
    const container = document.getElementById('emrbRoutineItems');
    const empty     = document.getElementById('emrbPanelEmpty');
    if (!container) return;

    if (this.routine.length === 0) {
      container.innerHTML = '';
      if (empty) empty.style.display = 'flex';
      return;
    }

    if (empty) empty.style.display = 'none';
    container.innerHTML = this.routine.map(p => `
      <div class="emrb-routine-item" data-id="${p.id}">
        ${p.image ? `<img class="emrb-routine-item__img" src="${p.image}" alt="${this.escape(p.title)}" loading="lazy">` : '<div class="emrb-routine-item__img"></div>'}
        <div class="emrb-routine-item__info">
          <p class="emrb-routine-item__title">${this.escape(p.title)}</p>
          <p class="emrb-routine-item__price">${p.price > 0 ? p.price.toFixed(2) + ' ' + this.currency : '—'}</p>
        </div>
        <button class="emrb-routine-item__remove" aria-label="Retirer ${this.escape(p.title)}" data-id="${p.id}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `).join('');

    container.querySelectorAll('.emrb-routine-item__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = parseInt(btn.dataset.id, 10);
        this.removeProduct(id);
      });
    });
  }

  updateProgress() {
    const count    = this.routine.length;
    const discount = this.getDiscount();
    const target   = 4;
    const pct      = Math.min((count / target) * 100, 100);

    const fill       = document.getElementById('emrbProgressFill');
    const text       = document.getElementById('emrbProgressText');
    const counter    = document.getElementById('emrbDiscountCounter');

    if (fill)    fill.style.width = `${pct}%`;
    if (text)    text.textContent = `${count} produit${count > 1 ? 's' : ''} sur 4`;

    if (counter) {
      const oldDiscount = parseInt(counter.dataset.discount || '0', 10);
      counter.dataset.discount = discount;
      counter.textContent = discount > 0 ? `-${discount}%` : 'Ajoute 2 soins';

      counter.classList.remove('emrb-discount-counter--gold', 'emrb-discount-counter--terracotta');
      if (discount >= 15) counter.classList.add('emrb-discount-counter--terracotta');
      else if (discount >= 10) counter.classList.add('emrb-discount-counter--gold');

      if (discount > oldDiscount) {
        counter.classList.add('emrb-discount-counter--active');
        setTimeout(() => counter.classList.remove('emrb-discount-counter--active'), 600);
      }
    }

    // Update hero step indicators
    document.querySelectorAll('.emrb-discount-step').forEach(step => {
      const stepDiscount = parseInt(step.dataset.discount, 10);
      step.classList.toggle('emrb-discount-step--active', discount >= stepDiscount);
    });
  }

  updatePriceSummary() {
    const count    = this.routine.length;
    const discount = this.getDiscount();
    const subtotal = this.routine.reduce((sum, p) => sum + p.price, 0);
    const savings  = subtotal * (discount / 100);
    const total    = subtotal - savings;

    const elSubtotal = document.getElementById('emrbSubtotal');
    const elSavings  = document.getElementById('emrbSavings');
    const elTotal    = document.getElementById('emrbTotal');
    const elPromo    = document.getElementById('emrbPromoCode');
    const elSavRow   = document.getElementById('emrbSavingsRow');

    if (elSubtotal) elSubtotal.textContent = `${subtotal.toFixed(2)} ${this.currency}`;
    if (elTotal)    elTotal.textContent    = `${total.toFixed(2)} ${this.currency}`;

    if (discount > 0) {
      if (elSavRow)    elSavRow.style.display   = 'flex';
      if (elSavings)   elSavings.textContent    = `-${savings.toFixed(2)} ${this.currency}`;
      if (elPromo) {
        const code = this.promoCodes[discount];
        elPromo.textContent = code;
        elPromo.style.display = 'inline-block';
      }
    } else {
      if (elSavRow)    elSavRow.style.display   = 'none';
      if (elPromo)     elPromo.style.display    = 'none';
    }
  }

  getDiscount() {
    const n = this.routine.length;
    if (n >= 4) return 15;
    if (n >= 3) return 10;
    if (n >= 2) return 5;
    return 0;
  }

  /* ────────────────────── COMPATIBILITY ────────────────────── */

  checkCompatibility() {
    const container = document.getElementById('emrbCompatibility');
    if (!container) return;

    const allActifs = this.routine.flatMap(p => p.tags.filter(t =>
      ['retinol','aha','bha','vitamine-c','acide-hyaluronique','niacinamide','bakuchiol'].includes(t)
    ));

    const alerts = this.compatibilityRules
      .filter(rule => rule.actifs.every(a => allActifs.includes(a)))
      .map(rule => rule.message);

    if (alerts.length > 0) {
      container.classList.add('emrb-compatibility--visible');
      container.innerHTML = alerts.map(msg =>
        `<div class="emrb-compatibility__alert">⚠ ${msg}</div>`
      ).join('');
    } else {
      container.classList.remove('emrb-compatibility--visible');
      container.innerHTML = '';
    }
  }

  /* ────────────────────── ADD TO CART ────────────────────── */

  updateAddCartBtn() {
    const btn = document.getElementById('emrbAddCartBtn');
    if (!btn) return;
    btn.disabled = this.routine.length === 0;
  }

  bindAddCartBtn() {
    const btn = document.getElementById('emrbAddCartBtn');
    if (!btn) return;
    btn.addEventListener('click', () => this.addAllToCart());
  }

  async addAllToCart() {
    if (this.routine.length === 0) return;

    const btn = document.getElementById('emrbAddCartBtn');
    if (btn) {
      btn.disabled     = true;
      btn.textContent  = 'Ajout en cours...';
    }

    try {
      const items = this.routine
        .filter(p => p.variantId)
        .map(p => ({ id: p.variantId, quantity: 1 }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw new Error('Erreur panier');

      // Apply discount via cart attributes
      const discount = this.getDiscount();
      if (discount > 0) {
        const code = this.promoCodes[discount];
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { '_routine_promo': code, '_routine_phase': this.phase || '' }
          })
        });
      }

      this.showConfirmation();
    } catch (err) {
      console.error('[EmmeraRoutineBuilder] cart error:', err);
      if (btn) {
        btn.disabled    = false;
        btn.textContent = 'Ajouter au panier';
      }
      alert('Une erreur est survenue. Réessaie dans un instant.');
    }
  }

  showConfirmation() {
    const builder = document.getElementById('emrbBuilderArea');
    const confirm = document.getElementById('emrbConfirmScreen');
    if (builder) builder.style.display = 'none';
    if (confirm) confirm.classList.add('emrb-confirm--visible');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const countEl = document.getElementById('emrbConfirmCount');
    const savEl   = document.getElementById('emrbConfirmSavings');
    const codeEl  = document.getElementById('emrbConfirmCode');

    if (countEl) countEl.textContent = this.routine.length;

    const discount = this.getDiscount();
    const savings  = this.routine.reduce((s, p) => s + p.price, 0) * (discount / 100);

    if (savEl && discount > 0) {
      savEl.textContent  = `${savings.toFixed(2)} ${this.currency} économisés`;
      savEl.style.display = 'block';
    }
    if (codeEl && discount > 0) {
      codeEl.textContent  = `Code promo ${this.promoCodes[discount]} appliqué automatiquement.`;
      codeEl.style.display = 'block';
    }
  }

  /* ────────────────────── SAVE ROUTINE ────────────────────── */

  bindSaveBtn() {
    const btn = document.getElementById('emrbSaveBtn');
    if (!btn) return;
    btn.addEventListener('click', () => this.saveRoutine());
  }

  saveRoutine() {
    if (this.routine.length === 0) return;
    const btn = document.getElementById('emrbSaveBtn');

    const saved = {
      phase:     this.phase,
      moment:    this.moment,
      products:  this.routine,
      savedAt:   Date.now(),
      name:      `Routine ${this.formatPhase(this.phase)} — ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
    };

    localStorage.setItem(this.savedKey, JSON.stringify(saved));

    if (btn) {
      btn.textContent = 'Routine sauvegardée';
      btn.classList.add('emrb-btn-save--saved');
    }

    this.showSavedStrip(saved);
  }

  loadSavedRoutine() {
    try {
      const raw = localStorage.getItem(this.savedKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.products) return;

      // Show restore strip
      this.showSavedStrip(saved);
    } catch {
      localStorage.removeItem(this.savedKey);
    }
  }

  showSavedStrip(saved) {
    const strip = document.getElementById('emrbSavedStrip');
    const name  = document.getElementById('emrbSavedName');
    const btn   = document.getElementById('emrbRestoreBtn');
    if (!strip) return;

    strip.classList.add('emrb-saved-strip--visible');
    if (name) name.textContent = saved.name || 'Routine sauvegardée';
    if (btn) {
      btn.addEventListener('click', () => this.restoreRoutine(saved), { once: true });
    }
  }

  restoreRoutine(saved) {
    this.routine = saved.products || [];

    // Restore phase
    if (saved.phase) {
      this.phase = saved.phase;
      const pill = document.querySelector(`.emrb-phase-pill[data-phase="${saved.phase}"]`);
      if (pill) {
        document.querySelectorAll('.emrb-phase-pill').forEach(p => p.classList.remove('emrb-pill--active'));
        pill.classList.add('emrb-pill--active');
        this.showPhaseHint();
        this.fetchAndRenderProducts().then(() => {
          this.routine.forEach(p => this.updateCardState(p.id, true));
        });
      }
    }

    // Restore moment
    if (saved.moment) {
      this.moment = saved.moment;
      document.querySelectorAll('.emrb-moment-pill').forEach(p => {
        p.classList.toggle('emrb-pill--active', p.dataset.moment === saved.moment);
      });
    }

    this.updatePanel();

    const strip = document.getElementById('emrbSavedStrip');
    if (strip) strip.classList.remove('emrb-saved-strip--visible');
  }

  /* ────────────────────── URL PARAM ────────────────────── */

  handleUrlParam() {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('add');
    if (!handle) return;

    // After products load, add matching product
    const tryAdd = () => {
      const product = this.allProducts.find(p => p.handle === handle);
      if (product) this.addProduct(product.id);
    };

    // Phase param
    const phase = params.get('phase');
    if (phase) {
      this.phase = phase;
      const pill = document.querySelector(`.emrb-phase-pill[data-phase="${phase}"]`);
      if (pill) {
        document.querySelectorAll('.emrb-phase-pill').forEach(p => p.classList.remove('emrb-pill--active'));
        pill.classList.add('emrb-pill--active');
        this.showPhaseHint();
      }
      this.fetchAndRenderProducts().then(tryAdd);
    }
  }

  /* ────────────────────── MOBILE PANEL ────────────────────── */

  bindPanelToggle() {
    const handle = document.getElementById('emrbPanelHandle');
    const panel  = document.getElementById('emrbPanel');
    if (!handle || !panel) return;

    handle.addEventListener('click', () => {
      panel.classList.toggle('emrb-panel--open');
    });
  }

  updateMobileHandle() {
    const countEl = document.getElementById('emrbMobileCount');
    if (countEl) countEl.textContent = this.routine.length;
  }

  /* ────────────────────── HELPERS ────────────────────── */

  formatPhase(phase) {
    const map = {
      menstruelle:  'Menstruelle',
      folliculaire: 'Folliculaire',
      ovulatoire:   'Ovulatoire',
      luteale:      'Lutéale'
    };
    return map[phase] || phase || '';
  }

  escape(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

/* ── Bootstrap ── */
document.addEventListener('DOMContentLoaded', () => {
  const builder = new EmmeraRoutineBuilder();
  builder.init();
  window.emmeraRoutineBuilder = builder;
});
